import React from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material";
import CopyIcon from "@mui/icons-material/CopyAll";
import CopyAll from "@mui/icons-material/CopyAll";



export interface HideSupportTileValues {
  icon?: boolean | undefined;
  name?: boolean | undefined;
  address?: boolean | undefined;
};

export interface SupportTileProps {
  icon?: string | undefined;
  name: string;
  address: string;
  qrCode: string;
  hide?: HideSupportTileValues | undefined;
  width?: string | undefined;
  height?: string | undefined;
  symbol?: string | undefined;
}

const tileStyle = {
  position: "absolute",
  width: "100%",
  height: "100%",
  backfaceVisibility: "hidden",
};

export type flipstate = "temporary" | "shown" | "hidden";

const SupportTile = ({
  icon,
  name,
  hide,
  address,
  qrCode,
  ...props
}: SupportTileProps) => {
  const theme = useTheme();

  const reverseSide = icon !== undefined;
  const [flipped, setFlipped] = React.useState<flipstate>(reverseSide ? "hidden" : "shown");

  const hideIcon = hide?.icon === true;
  const hideName = hide?.name === true;
  const hideAddress = hide?.address === true;
  
  const copyAddress = (address: string) => (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    navigator.clipboard.writeText(address);
  }

  const handleClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (!reverseSide) {
      return;
    }

    if (flipped === "shown") {
      setFlipped("hidden");
    } else {
      setFlipped("shown");
    }
  };


  const handleMouseOver =
    (state: flipstate) =>
    (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      if (!reverseSide) {
        return;
      }
      
      if (flipped === "shown") {
        return;
      }

      setFlipped(state);
    };

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <Box
        onClick={handleClick}
        onMouseEnter={handleMouseOver("temporary")}
        onMouseLeave={handleMouseOver("hidden")}
        sx={{
          cursor: 'pointer',
          position: "relative",
          width: "100%",
          height: "100%",
          textAlign: "center",
          transition: "all 800ms cubic-bezier(0.175, 0.885, 0.32, 1.275)",
          transformStyle: "preserve-3d",
          transform: `rotateY(${flipped !== "hidden" ? 360 : 0}deg)`,
          opacity: hideIcon ? 0 : 1,
        }}
      >
        <Stack
          sx={{
            ...tileStyle,
            opacity: flipped !== "hidden" ? 0 : 1,
          }}
        >
          <Typography fontWeight="bold">{name}</Typography>
          <img
            src={icon}
            alt={`${name} address: ${address}`}
            height={"auto"}
            width={props.width ?? "100%"}
            style={{
              border: `2px solid ${theme.palette.text.primary}`,
              borderRadius: "50%",
            }}
          />
        </Stack>

        <Stack
          sx={{
            ...tileStyle,
            opacity: flipped !== "hidden" ? 1 : 0,
          }}
        >
          <Typography fontWeight="bold" sx={{ opacity: hideName ? 0 : 1}} >{name}</Typography>
          <img
            src={qrCode}
            alt={`${name} address: ${address}`}
            height={ reverseSide ? "auto" : "100%"}
            width={reverseSide ? `calc(${props.height ?? "100%"} - 32px)` : "100%"}
          />
          <Button 
            disabled={ hideAddress }
            hidden={ hideAddress }
            sx={{ opacity: hideAddress ? 0 : 1 }}
            startIcon={ <CopyAll />}
            onClick={ copyAddress(address) }
          >
            Copy Address
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default SupportTile;
